import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { ClassificationInfo } from '../../types/api.js';

interface ClassificationResultProps {
  classification: ClassificationInfo;
  brandLabel: string;
  categoryKey: string | null;
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business AI判定結果表示コンポーネント。ブランド・カテゴリの既存/新規判定、確信度、判定理由を表示
 * @graph-connects none
 */
export function ClassificationResult({
  classification,
  brandLabel,
  categoryKey,
}: ClassificationResultProps) {
  const { t } = useTranslation();
  const [showReasoning, setShowReasoning] = useState(false);

  const confidencePercent = Math.round(classification.confidence * 100);

  return (
    <Card variant="glass" className="animate-fade-in">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
          <span className="text-base">🤖</span>
          {t('scoring.classification.title')}
        </h3>

        {/* 商品概要 */}
        {classification.productSummary && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {classification.productSummary}
          </p>
        )}

        {/* ブランド・カテゴリ判定 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* ブランド */}
          <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-surface)]">
            <span className="text-xs text-[var(--color-text-muted)]">{t('scoring.brand')}</span>
            <span className="text-sm font-medium text-[var(--color-text-primary)] flex-1">
              {brandLabel}
            </span>
            <StatusBadge isExisting={classification.isExistingBrand} />
          </div>

          {/* カテゴリ */}
          <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-surface)]">
            <span className="text-xs text-[var(--color-text-muted)]">{t('scoring.category')}</span>
            <span className="text-sm font-medium text-[var(--color-text-primary)] flex-1">
              {categoryKey ?? classification.nearestCategory ?? '不明'}
            </span>
            <StatusBadge isExisting={classification.isExistingCategory} />
          </div>
        </div>

        {/* 確信度バー */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('scoring.classification.confidence')}
            </span>
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">
              {String(confidencePercent)}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--color-bg-tertiary)] overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${String(confidencePercent)}%`,
                backgroundColor:
                  confidencePercent >= 80
                    ? 'var(--color-success)'
                    : confidencePercent >= 50
                      ? 'var(--color-warning)'
                      : 'var(--color-error)',
              }}
            />
          </div>
        </div>

        {/* 判定理由（折りたたみ） */}
        {classification.reasoning && (
          <div>
            <button
              onClick={() => {
                setShowReasoning(!showReasoning);
              }}
              className="text-xs text-[var(--color-accent-primary)] hover:underline cursor-pointer"
            >
              {showReasoning
                ? t('scoring.classification.hideReasoning')
                : t('scoring.classification.showReasoning')}
            </button>
            {showReasoning && (
              <p className="mt-2 text-xs text-[var(--color-text-muted)] p-2 rounded-lg bg-[var(--color-bg-surface)]">
                {classification.reasoning}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/** @graph-connects none */
function StatusBadge({ isExisting }: { isExisting: boolean }) {
  if (isExisting) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] border border-[var(--color-success)]/20">
        ✓ 既存
      </span>
    );
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-warning)]/10 text-[var(--color-warning)] border border-[var(--color-warning)]/20">
      ⚡ 推定
    </span>
  );
}
